import React, { useEffect, useMemo, useState } from "react";
import { formatDateTime } from "../utils/date";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { ROLES, ROLE_LABELS } from "../lib/rbac";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import {
    Eye, EyeOff, Plus, Pencil, KeyRound, PowerOff, Power, Trash2, RefreshCw, Search,
    Lock, LockOpen, LogOut as LogOutIcon, ShieldOff, Shield, Laptop,
} from "lucide-react";

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([id, label]) => ({ id, label }));

// ---------------------------------------------------------------------------
// Password strength (mirrors backend password_rules.py; must be >=8, upper,
// lower, digit, special. Score is 0..4.)
// ---------------------------------------------------------------------------
function scorePassword(pwd) {
    let s = 0;
    if (!pwd) return { score: 0, label: "—", checks: {} };
    const checks = {
        length: pwd.length >= 8,
        upper:  /[A-Z]/.test(pwd),
        lower:  /[a-z]/.test(pwd),
        digit:  /[0-9]/.test(pwd),
        special:/[^A-Za-z0-9]/.test(pwd),
    };
    s = Object.values(checks).filter(Boolean).length;
    const labels = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];
    return { score: s, label: labels[s], checks };
}

function StrengthMeter({ pwd }) {
    const { score, label, checks } = scorePassword(pwd);
    const width = `${(score / 5) * 100}%`;
    const color = score <= 2 ? "#A8324A" : score === 3 ? "#c69200" : "#1B7F4A";
    return (
        <div className="mt-1 space-y-1" data-testid="pwd-strength">
            <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full transition-all" style={{ width, background: color }} />
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px]">
                <span className="font-medium" style={{ color }}>{label}</span>
                {[
                    ["8+ chars",     checks.length],
                    ["upper",        checks.upper],
                    ["lower",        checks.lower],
                    ["digit",        checks.digit],
                    ["special",      checks.special],
                ].map(([k, ok]) => (
                    <span key={k} className={ok ? "text-emerald-700" : "text-gray-400"}>
                        {ok ? "✓" : "○"} {k}
                    </span>
                ))}
            </div>
        </div>
    );
}

function PasswordInput({ id, value, onChange, placeholder, testid }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <Input
                id={id}
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                autoComplete="new-password"
            />
            <button type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-800"
                data-testid={`${testid}-toggle`}
                aria-label={show ? "Hide password" : "Show password"}
            >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Add / Edit dialog
// ---------------------------------------------------------------------------
function UserFormDialog({ open, onClose, user, modules, onSaved }) {
    const isEdit = Boolean(user);
    const [form, setForm] = useState({
        name: "",
        email: "",
        role: ROLES.ASL_VIEWER,
        module_scope: "",
        password: "",
        must_change_password: true,
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                name: user?.name || "",
                email: user?.email || "",
                role: user?.role || ROLES.ASL_VIEWER,
                module_scope: user?.module_scope || "",
                password: "",
                must_change_password: true,
            });
        }
    }, [open, user]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const isDeptMgr = form.role === ROLES.ASL_DEPT_MGR;
    const strength = scorePassword(form.password);
    const passwordOk = !isEdit ? strength.score >= 5 : true;
    const canSave = form.name.trim().length >= 2
        && /\S+@\S+\.\S+/.test(form.email)
        && form.role
        && (!isDeptMgr || form.module_scope)
        && (isEdit || passwordOk);

    const save = async () => {
        setBusy(true);
        try {
            if (isEdit) {
                const payload = {
                    name: form.name.trim(),
                    email: form.email.trim().toLowerCase(),
                    role: form.role,
                    module_scope: isDeptMgr ? form.module_scope : null,
                };
                await api.patch(`/admin/users/${user.id}`, payload);
                toast.success("User updated");
            } else {
                const payload = {
                    name: form.name.trim(),
                    email: form.email.trim().toLowerCase(),
                    role: form.role,
                    module_scope: isDeptMgr ? form.module_scope : undefined,
                    password: form.password,
                    must_change_password: !!form.must_change_password,
                };
                await api.post("/admin/users", payload);
                toast.success("User created");
            }
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="user-form-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit user" : "Add user"}</DialogTitle>
                    <DialogDescription className="text-xs">
                        {isEdit
                            ? "Change name, email, role, or module scope. Password is reset separately."
                            : "Create a new user with a temporary password. Force them to change it on next login."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="uf-name">Full name</Label>
                        <Input id="uf-name" value={form.name} onChange={(e) => set({ name: e.target.value })}
                            placeholder="e.g. Amina Osman" data-testid="uf-name" />
                    </div>
                    <div>
                        <Label htmlFor="uf-email">Email</Label>
                        <Input id="uf-email" type="email" value={form.email}
                            onChange={(e) => set({ email: e.target.value })}
                            placeholder="user@asl.com" data-testid="uf-email" />
                    </div>
                    <div>
                        <Label>Role</Label>
                        <Select value={form.role} onValueChange={(v) => set({ role: v })}>
                            <SelectTrigger data-testid="uf-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ROLE_OPTIONS.map((r) => (
                                    <SelectItem key={r.id} value={r.id} data-testid={`uf-role-${r.id}`}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {isDeptMgr && (
                        <div>
                            <Label>Module scope</Label>
                            <Select value={form.module_scope} onValueChange={(v) => set({ module_scope: v })}>
                                <SelectTrigger data-testid="uf-module-scope"><SelectValue placeholder="Choose a module…" /></SelectTrigger>
                                <SelectContent>
                                    {modules.map((m) => (
                                        <SelectItem key={m.id} value={m.id} data-testid={`uf-module-${m.code}`}>
                                            {m.code} · {m.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-[color:var(--asl-muted)] mt-1">
                                A department manager only sees data inside this module.
                            </p>
                        </div>
                    )}
                    {!isEdit && (
                        <>
                            <div>
                                <Label htmlFor="uf-pwd">Temporary password</Label>
                                <PasswordInput id="uf-pwd" value={form.password}
                                    onChange={(v) => set({ password: v })}
                                    placeholder="min 8 chars, upper/lower/digit/special"
                                    testid="uf-password" />
                                <StrengthMeter pwd={form.password} />
                            </div>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.must_change_password}
                                    onChange={(e) => set({ must_change_password: e.target.checked })}
                                    data-testid="uf-must-change" />
                                <span>Force user to change password on next login</span>
                            </label>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="uf-cancel">Cancel</Button>
                    <Button className="asl-navy-bg text-white" onClick={save}
                        disabled={busy || !canSave} data-testid="uf-save">
                        {busy ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                        {isEdit ? "Save changes" : "Create user"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Reset password dialog
// ---------------------------------------------------------------------------
function ResetPasswordDialog({ open, onClose, user, onDone }) {
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [force, setForce] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setNewPwd(""); setConfirmPwd(""); setForce(true); }
    }, [open]);

    const strength = scorePassword(newPwd);
    const match = newPwd && newPwd === confirmPwd;
    const canSave = strength.score >= 5 && match;

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/users/${user.id}/reset-password`, {
                new_password: newPwd, force_change: force,
            });
            toast.success("Password reset. The user's active sessions have been signed out.");
            onDone?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    if (!user) return null;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="reset-pwd-dialog">
                <DialogHeader>
                    <DialogTitle>Reset password for {user.name}</DialogTitle>
                    <DialogDescription className="text-xs">
                        A new password will be set immediately. All active sessions for this user will be signed out.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="rp-new">New password</Label>
                        <PasswordInput id="rp-new" value={newPwd} onChange={setNewPwd}
                            placeholder="min 8 chars, upper/lower/digit/special"
                            testid="rp-new" />
                        <StrengthMeter pwd={newPwd} />
                    </div>
                    <div>
                        <Label htmlFor="rp-confirm">Confirm password</Label>
                        <PasswordInput id="rp-confirm" value={confirmPwd} onChange={setConfirmPwd}
                            placeholder="type it again" testid="rp-confirm" />
                        {confirmPwd && !match && (
                            <p className="text-[11px] text-red-600 mt-1">Passwords don&apos;t match.</p>
                        )}
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                            data-testid="rp-force" />
                        <span>Force user to change password on next login</span>
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="rp-cancel">Cancel</Button>
                    <Button className="asl-crimson-bg text-white" onClick={submit}
                        disabled={busy || !canSave} data-testid="rp-submit">
                        {busy ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-1" />}
                        Reset password
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Confirm dialog (suspend / reactivate / delete)
// ---------------------------------------------------------------------------
function ConfirmDialog({ open, onClose, title, description, confirmLabel, tone, onConfirm, testid }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try { await onConfirm(); onClose(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm" data-testid={testid}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription className="text-xs">{description}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid={`${testid}-cancel`}>Cancel</Button>
                    <Button
                        className={tone === "crimson" ? "asl-crimson-bg text-white" : "asl-navy-bg text-white"}
                        onClick={run} disabled={busy} data-testid={`${testid}-confirm`}>
                        {busy ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Users() {
    const { user: me } = useAuth();
    const { modules } = useModules();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("active");
    const [addOpen, setAddOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [resetUser, setResetUser] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const isAdmin = me?.role === ROLES.KAAFI_ADMIN;

    const load = async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 100 };
            if (query.trim()) params.q = query.trim();
            if (roleFilter !== "all") params.role = roleFilter;
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/admin/users", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [roleFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const modulesById = useMemo(() => Object.fromEntries((modules || []).map((m) => [m.id, m])), [modules]);

    // ------ actions ------
    const doSuspend = async (u) => {
        await api.post(`/admin/users/${u.id}/suspend`);
        toast.success(`${u.name} suspended`);
        await load();
    };
    const doReactivate = async (u) => {
        await api.post(`/admin/users/${u.id}/reactivate`);
        toast.success(`${u.name} reactivated`);
        await load();
    };
    const doDelete = async (u) => {
        await api.delete(`/admin/users/${u.id}`);
        toast.success(`${u.name} deleted`);
        await load();
    };
    // Iter78 — Kill-switch actions.
    const doForceLogout = async (u) => {
        await api.post(`/admin/users/${u.id}/force-logout`);
        toast.success(`${u.name} signed out of all devices`);
        await load();
    };
    const doUnlock = async (u) => {
        await api.post(`/admin/users/${u.id}/unlock`);
        toast.success(`${u.name} unlocked`);
        await load();
    };
    const [lockUser, setLockUser] = useState(null);
    const [regenUser, setRegenUser] = useState(null);
    const [regenCodes, setRegenCodes] = useState(null);
    const doLock = async (u, reason) => {
        await api.post(`/admin/users/${u.id}/lock`, { reason });
        toast.success(`${u.name} locked`);
        await load();
    };
    const doRegenerate = async (u) => {
        const { data } = await api.post(`/admin/users/${u.id}/regenerate-backup-codes`);
        setRegenCodes({ user: u, codes: data.backup_codes || [], kind: "backup" });
        toast.success(`Regenerated ${data.backup_codes?.length || 0} backup codes for ${u.name}`);
        await load();
    };
    // Iter79 — Admin regenerates emergency recovery codes for a user.
    const doRegenerateRecovery = async (u) => {
        const { data } = await api.post(`/admin/users/${u.id}/regenerate-recovery-codes`);
        setRegenCodes({ user: u, codes: data.recovery_codes || [], kind: "recovery" });
        toast.success(`Regenerated ${data.recovery_codes?.length || 0} recovery codes for ${u.name}`);
        await load();
    };
    // Iter81 — Admin revokes every trusted device for a user.
    const doRevokeTrustedDevices = async (u) => {
        const { data } = await api.post(`/admin/users/${u.id}/revoke-trusted-devices`);
        toast.success(`Revoked ${data?.revoked_count ?? 0} trusted device(s) for ${u.name}`);
        await load();
    };
    // Bulk selection
    const [selected, setSelected] = useState(new Set());
    const toggleSelect = (uid) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(uid)) next.delete(uid); else next.add(uid);
        return next;
    });
    const clearSelection = () => setSelected(new Set());
    const doBulkForceLogout = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        await api.post(`/admin/users/bulk-force-logout`, { user_ids: ids });
        toast.success(`Forced logout for ${ids.length} user${ids.length === 1 ? "" : "s"}`);
        clearSelection();
        await load();
    };

    if (!isAdmin) {
        return (
            <AppShell title="User Management">
                <div className="asl-card p-6 text-sm" data-testid="users-forbidden">
                    Only Kaafi System Admins can manage users.
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell title="User Management">
            <section className="space-y-5" data-testid="users-page">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <p className="text-sm text-[color:var(--asl-muted)] max-w-2xl">
                        Manage platform users, roles, and passwords. All actions are audit-logged; the last-remaining
                        System Admin cannot be removed or suspended.
                    </p>
                    <Button className="asl-navy-bg text-white" onClick={() => setAddOpen(true)} data-testid="users-add">
                        <Plus className="h-4 w-4 mr-1" /> Add user
                    </Button>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[220px] max-w-[360px]">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                            placeholder="Search name or email…"
                            className="pl-9"
                            data-testid="users-search"
                        />
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-[220px]" data-testid="users-role-filter">
                            <SelectValue placeholder="All roles" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All roles</SelectItem>
                            {ROLE_OPTIONS.map((r) => (
                                <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[160px]" data-testid="users-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                            <SelectItem value="deleted">Deleted</SelectItem>
                            <SelectItem value="all">All (incl. inactive)</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="text-xs text-[color:var(--asl-muted)] ml-auto" data-testid="users-count">
                        {total} user{total === 1 ? "" : "s"}
                    </span>
                </div>

                {loading ? (
                    <div className="asl-card p-6 text-sm text-[color:var(--asl-muted)]" data-testid="users-loading">
                        Loading users…
                    </div>
                ) : (
                    <div className="asl-card overflow-hidden">
                        <table className="asl-table asl-table--zebra" data-testid="users-table">
                            <thead
                                className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}
                            >
                                <tr>
                                    <th className="text-left px-5 py-3 w-[26%]">User</th>
                                    <th className="text-left px-5 py-3 w-[22%]">Email</th>
                                    <th className="text-left px-5 py-3">Role</th>
                                    <th className="text-left px-5 py-3">Module</th>
                                    <th className="text-left px-5 py-3">Status</th>
                                    <th className="text-left px-5 py-3">Last login</th>
                                    <th className="text-right px-5 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((u) => {
                                    const isSelf = u.id === me?.id;
                                    const isSuspended = u.active === false && !u.deleted_at;
                                    const isDeleted = !!u.deleted_at;
                                    const isLocked = !!u.locked_at;
                                    const initials = (u.name || u.email || "?").trim()
                                        .split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
                                    return (
                                        <tr
                                            key={u.id}
                                            className={`border-t ${isDeleted ? "opacity-60" : ""}`}
                                            style={{ borderColor: "var(--asl-border)" }}
                                            data-testid={`user-row-${u.email}`}
                                        >
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                        style={{ background: "#1B2A56" }}
                                                    >
                                                        {initials || "?"}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-medium asl-navy-text truncate">{u.name}</div>
                                                        {u.must_change_password && (
                                                            <div className="text-[10px] text-amber-700 font-medium">must change password</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 mono text-[13px] truncate">{u.email}</td>
                                            <td className="px-5 py-3">
                                                <span
                                                    className="text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap"
                                                    style={{ background: "var(--asl-navy-50)", color: "var(--asl-navy)" }}
                                                >
                                                    {ROLE_LABELS[u.role] || u.role}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 mono text-xs asl-crimson-text">
                                                {u.module_scope_code || (modulesById[u.module_scope]?.code) || "—"}
                                            </td>
                                            <td className="px-5 py-3">
                                                {isDeleted ? (
                                                    <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-600">Deleted</span>
                                                ) : isLocked ? (
                                                    <span
                                                        className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 inline-flex items-center gap-1"
                                                        title={u.locked_reason || "Account locked"}
                                                        data-testid={`user-locked-chip-${u.email}`}
                                                    >
                                                        <Lock className="h-3 w-3" /> Locked
                                                    </span>
                                                ) : isSuspended ? (
                                                    <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800">Suspended</span>
                                                ) : (
                                                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">Active</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-xs text-[color:var(--asl-muted)]">
                                                {u.last_login_at ? formatDateTime(u.last_login_at) : "Never"}
                                            </td>
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                <div className="inline-flex gap-1">
                                                    <Button
                                                        variant="ghost" size="sm"
                                                        onClick={() => setEditUser(u)}
                                                        disabled={isDeleted}
                                                        title="Edit user"
                                                        data-testid={`user-edit-${u.email}`}
                                                    ><Pencil className="h-4 w-4" /></Button>
                                                    <Button
                                                        variant="ghost" size="sm"
                                                        onClick={() => setResetUser(u)}
                                                        disabled={isDeleted}
                                                        title="Reset password"
                                                        data-testid={`user-reset-${u.email}`}
                                                    ><KeyRound className="h-4 w-4" /></Button>
                                                    {/* Iter78 — Force logout (kill-switch) */}
                                                    {!isSelf && !isDeleted && (
                                                        <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                            title: `Sign ${u.name} out of all devices?`,
                                                            description: "Every active session token for this user will be invalidated. They can log in again immediately.",
                                                            confirmLabel: "Force logout",
                                                            tone: "crimson",
                                                            onConfirm: () => doForceLogout(u),
                                                            testid: `user-force-logout-${u.email}`,
                                                        })}
                                                        title="Force logout"
                                                        data-testid={`user-force-logout-btn-${u.email}`}
                                                        ><LogOutIcon className="h-4 w-4" /></Button>
                                                    )}
                                                    {/* Iter78 — Regenerate MFA backup codes */}
                                                    {!isDeleted && (
                                                        <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                            title: `Regenerate backup codes for ${u.name}?`,
                                                            description: "A fresh set of 8 backup codes will be generated. Previous codes stop working immediately. You'll see the new codes ONCE — save them securely.",
                                                            confirmLabel: "Regenerate codes",
                                                            onConfirm: () => doRegenerate(u),
                                                            testid: `user-regen-codes-${u.email}`,
                                                        })}
                                                        title="Regenerate MFA backup codes"
                                                        data-testid={`user-regen-btn-${u.email}`}
                                                        ><ShieldOff className="h-4 w-4" /></Button>
                                                    )}
                                                    {/* Iter79 — Regenerate emergency recovery codes */}
                                                    {!isDeleted && (
                                                        <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                            title: `Regenerate recovery codes for ${u.name}?`,
                                                            description: "A fresh set of 5 emergency recovery codes will be generated. Previous recovery codes stop working immediately. You'll see the new codes ONCE — hand them to the user securely.",
                                                            confirmLabel: "Regenerate recovery codes",
                                                            tone: "crimson",
                                                            onConfirm: () => doRegenerateRecovery(u),
                                                            testid: `user-regen-recovery-${u.email}`,
                                                        })}
                                                        title="Regenerate emergency recovery codes"
                                                        data-testid={`user-regen-recovery-btn-${u.email}`}
                                                        ><KeyRound className="h-4 w-4 text-rose-600" /></Button>
                                                    )}
                                                    {/* Iter81 — Revoke all trusted devices */}
                                                    {!isDeleted && (
                                                        <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                            title: `Revoke trusted devices for ${u.name}?`,
                                                            description: "All 7-day 'trust this device' cookies for this user will be invalidated. Their next login on any browser will require the full 6-digit MFA code again.",
                                                            confirmLabel: "Revoke trusted devices",
                                                            tone: "crimson",
                                                            onConfirm: () => doRevokeTrustedDevices(u),
                                                            testid: `user-revoke-trusted-${u.email}`,
                                                        })}
                                                        title="Revoke trusted devices"
                                                        data-testid={`user-revoke-trusted-btn-${u.email}`}
                                                        ><Laptop className="h-4 w-4 text-rose-600" /></Button>
                                                    )}
                                                    {/* Iter78 — Lock / Unlock */}
                                                    {!isSelf && !isDeleted && (
                                                        isLocked ? (
                                                            <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                                title: `Unlock ${u.name}?`,
                                                                description: "The user will be able to log in again once their sessions restart.",
                                                                confirmLabel: "Unlock",
                                                                onConfirm: () => doUnlock(u),
                                                                testid: `user-unlock-${u.email}`,
                                                            })}
                                                            title="Unlock account"
                                                            data-testid={`user-unlock-btn-${u.email}`}
                                                            ><LockOpen className="h-4 w-4 text-amber-700" /></Button>
                                                        ) : (
                                                            <Button variant="ghost" size="sm"
                                                                onClick={() => setLockUser(u)}
                                                                title="Lock account"
                                                                data-testid={`user-lock-btn-${u.email}`}
                                                            ><Lock className="h-4 w-4" /></Button>
                                                        )
                                                    )}
                                                    {!isSelf && !isDeleted && (
                                                        isSuspended ? (
                                                            <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                                title: `Reactivate ${u.name}?`,
                                                                description: "They will be able to log in again.",
                                                                confirmLabel: "Reactivate",
                                                                onConfirm: () => doReactivate(u),
                                                                testid: `user-reactivate-${u.email}`,
                                                            })}
                                                            title="Reactivate"
                                                            data-testid={`user-reactivate-btn-${u.email}`}
                                                            ><Power className="h-4 w-4" /></Button>
                                                        ) : (
                                                            <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                                title: `Suspend ${u.name}?`,
                                                                description: "Their active sessions will end immediately and they won't be able to log in until reactivated.",
                                                                confirmLabel: "Suspend",
                                                                tone: "crimson",
                                                                onConfirm: () => doSuspend(u),
                                                                testid: `user-suspend-${u.email}`,
                                                            })}
                                                            title="Suspend"
                                                            data-testid={`user-suspend-btn-${u.email}`}
                                                            ><PowerOff className="h-4 w-4" /></Button>
                                                        )
                                                    )}
                                                    {!isSelf && !isDeleted && (
                                                        <Button variant="ghost" size="sm" onClick={() => setConfirm({
                                                            title: `Delete ${u.name}?`,
                                                            description: "The user will be removed from listings. Data they authored is retained.",
                                                            confirmLabel: "Delete",
                                                            tone: "crimson",
                                                            onConfirm: () => doDelete(u),
                                                            testid: `user-delete-${u.email}`,
                                                        })}
                                                        title="Delete"
                                                        data-testid={`user-delete-btn-${u.email}`}
                                                        ><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-5 py-10 text-center text-sm text-[color:var(--asl-muted)]">
                                            No users match the current filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <UserFormDialog
                open={addOpen} onClose={() => setAddOpen(false)}
                user={null} modules={modules || []} onSaved={load}
            />
            <UserFormDialog
                open={!!editUser} onClose={() => setEditUser(null)}
                user={editUser} modules={modules || []} onSaved={load}
            />
            <ResetPasswordDialog
                open={!!resetUser} onClose={() => setResetUser(null)}
                user={resetUser} onDone={load}
            />
            {confirm && (
                <ConfirmDialog
                    open={!!confirm} onClose={() => setConfirm(null)}
                    title={confirm.title} description={confirm.description}
                    confirmLabel={confirm.confirmLabel} tone={confirm.tone}
                    onConfirm={confirm.onConfirm} testid={confirm.testid}
                />
            )}
            {lockUser && (
                <LockUserDialog
                    user={lockUser}
                    onClose={() => setLockUser(null)}
                    onSubmit={async (reason) => {
                        await doLock(lockUser, reason);
                        setLockUser(null);
                    }}
                />
            )}
            {regenCodes && (
                <BackupCodesDialog
                    user={regenCodes.user}
                    codes={regenCodes.codes}
                    kind={regenCodes.kind || "backup"}
                    onClose={() => setRegenCodes(null)}
                />
            )}
        </AppShell>
    );
}

// ---------------------------------------------------------------------------
// Iter78 — Lock user dialog (typed reason)
// ---------------------------------------------------------------------------
function LockUserDialog({ user, onClose, onSubmit }) {
    const [reason, setReason] = useState("");
    const [confirmTxt, setConfirmTxt] = useState("");
    const [busy, setBusy] = useState(false);
    const canConfirm = reason.trim().length >= 3 && confirmTxt === "LOCK";
    const submit = async () => {
        if (!canConfirm) return;
        setBusy(true);
        try { await onSubmit(reason.trim()); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid={`user-lock-dialog-${user.email}`}>
                <DialogHeader>
                    <DialogTitle>Lock {user.name}?</DialogTitle>
                    <DialogDescription className="text-xs">
                        The user will be blocked from logging in with a generic 423 response.
                        Their active sessions are revoked immediately. Type <code>LOCK</code> to confirm.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label htmlFor="lock-reason" className="text-xs">Reason</Label>
                        <Input
                            id="lock-reason" value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Suspicious activity — SOC review"
                            data-testid="user-lock-reason"
                        />
                    </div>
                    <div>
                        <Label htmlFor="lock-confirm" className="text-xs">Type LOCK</Label>
                        <Input
                            id="lock-confirm" value={confirmTxt}
                            onChange={(e) => setConfirmTxt(e.target.value)}
                            data-testid="user-lock-confirm-input"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}
                        data-testid="user-lock-cancel">Cancel</Button>
                    <Button
                        className="asl-crimson-bg text-white"
                        onClick={submit} disabled={!canConfirm || busy}
                        data-testid="user-lock-confirm">
                        {busy ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                        Lock account
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Iter78 — Backup codes reveal dialog (show once, copy-to-clipboard)
// ---------------------------------------------------------------------------
function BackupCodesDialog({ user, codes, kind = "backup", onClose }) {
    const copyAll = () => {
        try {
            navigator.clipboard.writeText(codes.join("\n"));
            toast.success("Codes copied to clipboard");
        } catch { toast.error("Copy failed"); }
    };
    const isRecovery = kind === "recovery";
    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="user-backup-codes-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {isRecovery ? "Emergency recovery" : "Backup"} codes for {user.name}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-rose-700">
                        Save these codes now — they will NOT be shown again.
                        {isRecovery
                            ? " Each code resets the user's password AND clears MFA AND revokes every session. A CRITICAL alert is sent to admins whenever one is used."
                            : " Each code works exactly once for MFA challenge bypass."}
                    </DialogDescription>
                </DialogHeader>
                <div className={`${isRecovery ? "bg-rose-50 border-rose-300" : "bg-slate-50"} border rounded p-3 grid ${isRecovery ? "grid-cols-1" : "grid-cols-2"} gap-2 font-mono text-xs`}>
                    {codes.map((c, i) => (
                        <div key={c} data-testid={`${isRecovery ? "recovery-code" : "backup-code"}-${i}`}>{c}</div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={copyAll} data-testid="backup-codes-copy">
                        Copy all
                    </Button>
                    <Button className="asl-navy-bg text-white" onClick={onClose} data-testid="backup-codes-done">
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
